<script setup lang="ts">
import {
  EntityInsuranceProductItem,
  EntityInsuranceProductItemSkeleton,
  type IInsuranceProduct
} from '~/components/entity/insurance-product'
import type { IResponseData } from '~/types'
import { FeaturePagination } from '~/components/feature/pagination'
import { useFetchLocale } from '~/composables/useFetchLocale'

interface IProps {
  insuranceCompanyId: number
  perPage?: number
}

const props = withDefaults(defineProps<IProps>(), {
  perPage: 10
})

const emit = defineEmits<{
  loaded: [hasData: boolean]
}>()

const config = useRuntimeConfig()
const baseApiUrl = config.public.baseApiUrl

const page = ref(1)

const query = computed(() =>
  new URLSearchParams({
    'filter[insurance_company_id]': String(props.insuranceCompanyId),
    page: String(page.value),
    per_page: String(props.perPage)
  }).toString()
)

const url = computed(() => `${baseApiUrl}/insurance_products?${query.value}`)

const {
  data: products,
  status,
  refresh
} = useFetchLocale<IResponseData<IInsuranceProduct[]>>(url, {
  key: computed(() => `insurance-products-${props.insuranceCompanyId}-${page.value}`),
  watch: false,
  immediate: true
})

watchEffect(() => {
  if (status.value === 'success') {
    emit('loaded', !!products.value?.data?.length)
  }
})

watch(url, () => {
  refresh()
})

const fetchProducts = (pageNumber: number) => {
  page.value = pageNumber
}
</script>

<template>
  <section v-if="status === 'success'">
    <div v-if="products?.data?.length" class="CardsList">
      <EntityInsuranceProductItem v-for="item in products.data" :key="item.id" :item="item" />
      <div v-if="products.meta.last_page !== 1" class="mt-7 flex justify-end">
        <FeaturePagination :meta="products.meta" :links="products.links" @change="fetchProducts" />
      </div>
    </div>
    <Alert v-else variant="blue">
      <AlertDescription>{{ $t('widget.insurance_products.list.no_products') }}</AlertDescription>
    </Alert>
  </section>

  <section v-else>
    <EntityInsuranceProductItemSkeleton :count="props.perPage" />
  </section>
</template>
